import { QURAN_CREDIT, WORDS_CREDIT } from '@iqrapath/shared'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { AudioButton } from '../../components/AudioButton.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { useLatinOn } from '../../lib/prefs.ts'
import { PassageBlock } from '../pegangan/PassageBlock.tsx'
import type { MutiaraCard } from './mutiara.ts'

/** The content of one Mutiara card (feed and Kotak). Every text is shown as the dataset gives it, with its source link. */
export function MutiaraCardBody({ c }: { c: MutiaraCard }) {
  const latin = useLatinOn()
  switch (c.jenis) {
    case 'ayat':
      return (
        <>
          <PassageBlock p={c.ayat} id={`mt-${c.id}`} what={`ayat ${c.ayat.ref}`} />
          <div className="mt-tafsir">
            <h3>Tafsir Ringkas Kemenag</h3>
            <p>{c.tafsir}</p>
          </div>
          <p className="credit"><SourceLink href={c.ayat.url}>{QURAN_CREDIT}</SourceLink></p>
        </>
      )
    case 'hadis':
      return (
        <figure className="mt-hadis">
          <h3 className="mt-h">{c.title}</h3>
          <span className="pill pill-mint">{c.grade}</span>
          {/* verbatim from HadeethEnc (their terms: no changes) */}
          <blockquote>{c.text}</blockquote>
          {c.faedah && (
            <div className="mt-tafsir">
              <h3>Faedah hadis</h3>
              <p>{c.faedah}</p>
            </div>
          )}
          <figcaption>
            <span>{c.attribution}</span>
            <SourceLink href={c.url}>{c.source}</SourceLink>
          </figcaption>
        </figure>
      )
    case 'kata':
      return (
        <>
          <div className="mt-word">
            <p className="ar" lang="ar" dir="rtl">{c.ar}</p>
            {latin && c.latin && <p className="lat">{c.latin}</p>}
            <p className="mt-arti">{c.arti}</p>
            {c.audio && <AudioButton id={`mt-${c.id}-w`} srcs={[c.audio]} what={`kata ${c.arti}`} />}
          </div>
          <p className="mt-count">Bentuk kata ini muncul di <b>{c.count} ayat</b> Al-Qur’an.</p>
          <div className="mt-contoh">
            <h3>Salah satunya</h3>
            <p className="ar" lang="ar" dir="rtl">
              {c.contoh.words.map((w, i) => (
                <Fragment key={i}>{i > 0 && ' '}{i === c.contoh.at ? <mark>{w}</mark> : w}</Fragment>
              ))}
            </p>
            <p className="pg-tr">{c.contoh.terjemah}</p>
            <div className="pg-ref-row">
              <SourceLink href={c.contoh.url} className="pg-ref">{c.contoh.ref}</SourceLink>
              {c.contoh.audio && <AudioButton id={`mt-${c.id}-v`} srcs={[c.contoh.audio]} what={`ayat ${c.contoh.ref}`} />}
            </div>
          </div>
          <p className="credit">
            <SourceLink href={c.contoh.wordsUrl}>{WORDS_CREDIT}</SourceLink> · <SourceLink href={c.contoh.url}>Terjemah: Qur’an Kemenag</SourceLink>
          </p>
        </>
      )
    case 'pegangan': {
      const tr = c.ayat.verses?.map((v) => v.terjemah).join(' ')
      return (
        <>
          <h3 className="mt-h">{c.judul}</h3>
          <p className="muted">{c.ringkas}</p>
          {tr && <p className="pg-tr">{tr}</p>}
          <p className="mt-src"><SourceLink href={c.ayat.url} className="pg-ref">{c.ayat.ref}</SourceLink></p>
          <p className="mt-step"><span className="pg-when">Sekarang</span>{c.langkah}</p>
          {!c.reviewed && <p className="help">{c.ai ? 'Draf AI, menunggu tinjauan ustadz.' : 'Draf, menunggu tinjauan ustadz.'}</p>}
          <Link className="btn btn-ghost btn-sm mt-open" to={`/pegangan/${c.slug}`}>Buka kartu lengkap</Link>
        </>
      )
    }
  }
}
